/**
 * UpgradeGate — verrouille une section et invite à upgrader.
 *
 * Usage :
 *   <UpgradeGate feature="focusMode" requiredPlan="essential">
 *     <FocusWorkScreen />
 *   </UpgradeGate>
 */
import type { JSX, ReactNode } from 'react';
import { Lock, Sparkles } from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { useNavStore } from '@store/navStore';
import { usePlan } from '@/hooks/usePlan';
import type { PlanKey } from '@/lib/plans';
import { PLAN_LABELS } from '@/lib/plans';

interface UpgradeGateProps {
  /** Clé de la feature dans PLAN_LIMITS. */
  feature: string;
  /** Plan minimum requis. */
  requiredPlan?: PlanKey;
  /** Contenu à afficher si le plan est suffisant. */
  children: ReactNode;
  /** Message personnalisé affiché sous le titre. */
  message?: string;
}

export function UpgradeGate({
  feature,
  requiredPlan = 'essential',
  children,
  message,
}: UpgradeGateProps): JSX.Element {
  const { limits } = usePlan();
  const navigate = useNavStore((s) => s.navigate);

  const hasAccess = Boolean(limits[feature as keyof typeof limits]);

  if (hasAccess) return <>{children}</>;

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-8 text-center">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
        style={{ background: 'var(--color-primary-light)' }}
      >
        <Lock size={28} style={{ color: 'var(--color-primary-dark)' }} />
      </div>

      <h2 className="!text-xl !border-none !pb-0 mb-2">Fonctionnalité verrouillée</h2>

      <p
        className="text-sm max-w-sm mb-6"
        style={{ color: 'var(--color-text-medium)' }}
      >
        {message ??
          `Cette fonctionnalité est disponible à partir du plan ${PLAN_LABELS[requiredPlan]}.`}
      </p>

      <div className="flex gap-3 flex-wrap justify-center">
        <UniqueButton
          leftIcon={<Sparkles size={16} />}
          onClick={() => navigate('pricing')}
        >
          Voir les offres
        </UniqueButton>
        <UniqueButton variant="secondary" onClick={() => navigate('dashboard')}>
          Retour au tableau de bord
        </UniqueButton>
      </div>
    </div>
  );
}

/** Variante inline : affiche une bannière sans bloquer tout l'écran. */
export function UpgradeBanner({
  message,
  requiredPlan = 'essential',
}: {
  message: string;
  requiredPlan?: PlanKey;
}): JSX.Element {
  const navigate = useNavStore((s) => s.navigate);

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-card px-4 py-3 mb-4"
      style={{
        background: 'var(--color-primary-glow)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div className="flex items-center gap-2">
        <Lock size={14} style={{ color: 'var(--color-primary-dark)', flexShrink: 0 }} />
        <span className="text-sm" style={{ color: 'var(--color-text-dark)' }}>
          {message}{' '}
          <span style={{ color: 'var(--color-text-medium)' }}>
            (plan {PLAN_LABELS[requiredPlan]} requis)
          </span>
        </span>
      </div>
      <UniqueButton size="sm" onClick={() => navigate('pricing')}>
        Upgrader
      </UniqueButton>
    </div>
  );
}
